import { z } from 'zod'

/**
 * `operacoes.projects.visible_sections` (JSONB)
 * Controla quais seções do portal o cliente vê para este projeto.
 *
 * Nova seção: adicionar aqui + na UI do ERP que edita esse JSONB.
 */
const initiativeFlagsSchema = z
  .object({
    discovery: z.boolean().optional(),
    progress: z.boolean().optional(),
    docs: z.boolean().optional(),
    // A aba unificada. Quando presente, manda sozinha.
    atendimento: z.boolean().optional(),
    // `validation` e `suporte` são as duas abas que viraram Atendimento. Ficam
    // aqui porque projetos existentes já têm esses valores gravados: enquanto
    // `atendimento` não for definida, elas continuam decidindo.
    validation: z.boolean().optional(),
    // `suporte` era lida pelo Layout e só sobrevivia pelo `.passthrough()`:
    // não estava no schema nem na UI do ERP, então o time não conseguia
    // ligar ou desligar a aba sem editar o JSONB na mão.
    suporte: z.boolean().optional(),
  })
  .passthrough()

export const visibleSectionsSchema = z
  .object({
    dashboard: z.boolean().default(true),
    progress: z.boolean().default(true),
    testing: z.boolean().default(true),
    docs: z.boolean().default(true),
    // discovery sections (futuro - sub-spec 06)
    discovery_overview: z.boolean().default(false),
    discovery_modules: z.boolean().default(false),
    discovery_journey: z.boolean().default(false),
    discovery_architecture: z.boolean().default(false),
    discovery_milestones: z.boolean().default(false),
    // matriz por iniciativa (Parte 1 do redesign). Fallback global em
    // `initiatives`, override por id em `initiatives_by_id[<initiativeId>]`.
    initiatives: initiativeFlagsSchema.optional(),
    initiatives_by_id: z.record(z.string(), initiativeFlagsSchema).optional(),
  })
  .passthrough()

export type InitiativeFlags = z.infer<typeof initiativeFlagsSchema>
export type VisibleSections = z.infer<typeof visibleSectionsSchema>

export function parseVisibleSections(input: unknown): VisibleSections {
  return visibleSectionsSchema.parse(input ?? {})
}

/**
 * Resolve flags de iniciativa: usa override por id se existir, senao cai no
 * fallback global `initiatives`, senao usa defaults conservadores.
 */
export function resolveInitiativeFlags(
  visible: VisibleSections | null | undefined,
  initiativeId: number | string | null | undefined,
): InitiativeFlags {
  const defaults: InitiativeFlags = {
    discovery: false,
    progress: true,
    docs: false,
    validation: false,
  }
  if (!visible) return defaults
  const globalFlags = (visible.initiatives ?? {}) as InitiativeFlags
  const overrides =
    initiativeId != null
      ? ((visible.initiatives_by_id?.[String(initiativeId)] ?? {}) as InitiativeFlags)
      : {}
  return { ...defaults, ...globalFlags, ...overrides }
}
